import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import Dialog from '../../../components/ui/Dialog';
import Tooltip from '../../../components/ui/Tooltip';
import ErrorState from '../../../components/ui/ErrorState';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import NameWithEmailTooltip from '../../../components/ui/NameWithEmailTooltip';
import { publicFeedbackService, type PublicFeedbackDetails } from '../../../services';
import arrowBackIcon from '../../../assets/arrow_back.svg';

const DEFAULT_CRITERIA = [
  { id: '1', description: 'Solution Focused', tooltip: 'I prioritise customer needs through value-added solutions.' },
  { id: '2', description: 'Adapt to Win', tooltip: 'I learn, unlearn and re-learn to succeed.' },
  { id: '3', description: 'Courage to Act', tooltip: 'I speak up, challenge norms, and act with purpose and pace.' },
  { id: '4', description: 'Act for Enterprise', tooltip: 'I act on what\'s best for PETRONAS.' },
  { id: '5', description: 'Own It', tooltip: 'I am accountable to deliver outcomes that matter.' },
];

const External360FeedbackDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { nomination_relationship_id: relationshipId = '', tab = 'review' } = useParams();
  const [searchParams] = useSearchParams();
  const uuid = searchParams.get('uuid')?.trim() || '';
  const [details, setDetails] = useState<PublicFeedbackDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [appreciative, setAppreciative] = useState('');
  const [constructive, setConstructive] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dialog, setDialog] = useState({ isOpen: false, type: 'info' as 'success' | 'error' | 'warning' | 'info', title: '', message: '' });

  useEffect(() => {
    let isActive = true;

    if (!uuid || !relationshipId) {
      setDetails(null);
      setApiError(!uuid ? 'A reviewer UUID is required to load feedback.' : 'A nomination relationship ID is required.');
      setLoading(false);
      return () => {
        isActive = false;
      };
    }

    setLoading(true);
    setApiError(null);

    const loadDetails = async () => {
      try {
        const listResponse = await publicFeedbackService.getNominations(uuid);
        const nominator = listResponse.data?.nominators?.find(
          (item) => String(item.nomination_relationship_id) === relationshipId,
        );

        if (!listResponse.status || !nominator) {
          throw new Error(listResponse.message || listResponse.error || 'Feedback nomination not found.');
        }

        const relationshipType = nominator.relationship_type?.trim().toLowerCase().replace(/[\s-]+/g, '_');
        const nominationAs = relationshipType === 'peer' ? 'Peer'
          : relationshipType === 'superior' ? 'Superior'
            : relationshipType === 'subordinate' ? 'Subordinate'
              : relationshipType === 'additional_subordinate' ? 'Additional Subordinate'
              : relationshipType === 'dotted_superior' ? 'Additional Superior'
              : 'Stakeholder';
        let loadedDetails: PublicFeedbackDetails = {
          employee: {
            employeeId: nominator.employee_id || '',
            employeeName: nominator.nominator_name || nominator.nominator_email || 'Unknown',
            employeeEmail: nominator.nominator_email || '',
            designation: nominator.nominator_designation || '',
            department: nominator.nominator_department || '',
            nominationAs,
            reportingManager: nominator.nominator_manager?.manager_name || '',
            reportingManagerEmail: nominator.nominator_manager?.manager_email || '',
          },
          criteria: DEFAULT_CRITERIA,
          appreciativeFeedback: '',
          constructiveFeedback: '',
          submitted: Boolean(nominator.review_exists),
        };

        try {
          const reviewResponse = await publicFeedbackService.getReview(uuid, relationshipId);
          if (reviewResponse.status && reviewResponse.data) {
            const review = reviewResponse.data;
            const competencyKeys: Record<string, string> = {
              '1': 'solution_focused',
              '2': 'adapt_to_win',
              '3': 'courage_to_act',
              '4': 'act_for_enterprise',
              '5': 'own_it',
            };
            const status = review.status?.toLowerCase();
            const reviewRelationshipType = review.relationship_type?.trim().toLowerCase().replace(/[\s-]+/g, '_');

            loadedDetails = {
              employee: {
                employeeId: String(review.reviewee?.employee_id || loadedDetails.employee.employeeId),
                employeeName: review.reviewee?.name || review.reviewee?.email || loadedDetails.employee.employeeName,
                employeeEmail: review.reviewee?.email || loadedDetails.employee.employeeEmail,
                designation: review.reviewee?.designation || loadedDetails.employee.designation,
                department: review.reviewee?.department || loadedDetails.employee.department,
                nominationAs: reviewRelationshipType === 'dotted_superior'
                  ? 'Additional Superior'
                  : reviewRelationshipType === 'additional_subordinate'
                    ? 'Additional Subordinate'
                    : review.relationship_type
                    ? review.relationship_type.charAt(0).toUpperCase() + review.relationship_type.slice(1)
                  : loadedDetails.employee.nominationAs,
                reportingManager: review.reviewee?.manager?.manager_name || loadedDetails.employee.reportingManager,
                reportingManagerEmail: review.reviewee?.manager?.manager_email || loadedDetails.employee.reportingManagerEmail,
              },
              criteria: DEFAULT_CRITERIA.map((criterion) => {
                const rating = review.competency?.[competencyKeys[criterion.id] as keyof typeof review.competency];
                return { ...criterion, rating: typeof rating === 'number' ? rating : undefined };
              }),
              appreciativeFeedback: review.feedback?.appreciative || '',
              constructiveFeedback: review.feedback?.constructive || '',
              submitted: Boolean(nominator.review_exists) || ['submitted', 'completed', 'approved'].includes(status || ''),
            };
          } else if (tab === 'view') {
            throw new Error(reviewResponse.message || reviewResponse.error || 'Submitted feedback could not be loaded.');
          }
        } catch (error) {
          if (tab === 'view') throw error;
        }

        if (!isActive) return;
        setDetails(loadedDetails);
        setRatings(Object.fromEntries(
          loadedDetails.criteria
            .filter((criterion) => typeof criterion.rating === 'number')
            .map((criterion) => [criterion.id, criterion.rating as number]),
        ));
        setAppreciative(loadedDetails.appreciativeFeedback);
        setConstructive(loadedDetails.constructiveFeedback);
      } catch (error) {
        if (!isActive) return;
        setDetails(null);
        setApiError(error instanceof Error ? error.message : 'Unable to load feedback details.');
      } finally {
        if (isActive) setLoading(false);
      }
    };

    void loadDetails();
    return () => {
      isActive = false;
    };
  }, [uuid, relationshipId, reloadKey, tab]);

  if (loading) return <div className="flex h-full items-center justify-center"><LoadingSpinner /></div>;
  if (apiError || !details) {
    return (
      <ErrorState
        title={uuid && relationshipId ? 'Unable to Load Feedback' : 'Feedback Link Incomplete'}
        message={apiError || 'Feedback details could not be loaded.'}
        onRetry={uuid && relationshipId ? () => setReloadKey((current) => current + 1) : undefined}
        showRetryButton={Boolean(uuid && relationshipId)}
      />
    );
  }

  const criteria = details.criteria;
  const isReadOnly = tab === 'view' || details.submitted;
  const isFeedbackValid = (feedback: string) => {
    const length = feedback.trim().length;
    return length >= 10 && length <= 10000;
  };
  const isComplete = criteria.every((criterion) => {
    const rating = ratings[criterion.id];
    return typeof rating === 'number' && rating >= 1 && rating <= 4;
  }) && isFeedbackValid(appreciative) && isFeedbackValid(constructive);
  const canSaveDraft = Object.keys(ratings).length > 0 || [appreciative, constructive].some(isFeedbackValid);

  const save = async (submit: boolean) => {
    if (submit && !isComplete) {
      setDialog({ isOpen: true, type: 'warning', title: 'Incomplete Form', message: 'Please select every rating and provide at least 10 characters in both feedback fields.' });
      return;
    }

    if (!submit && !canSaveDraft) {
      setDialog({ isOpen: true, type: 'warning', title: 'Nothing to Save', message: 'Add a rating or at least 10 characters of feedback before saving your draft.' });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = submit
        ? await publicFeedbackService.submitReview(uuid, relationshipId, appreciative, constructive, ratings)
        : await publicFeedbackService.saveReview(uuid, relationshipId, appreciative, constructive, ratings);

      if (!response.status) {
        throw new Error(response.message || response.error || 'Unable to save feedback. Please try again.');
      }

      if (submit) {
        setDetails((current) => current ? { ...current, submitted: true } : current);
      }

      const responseMessage = response.message || '';
      const draftSaveKeywords = ['saved', 'updated', 'created', 'stored', 'draft'];
      const isDraftSaveMessage = draftSaveKeywords.some((keyword) =>
        responseMessage.toLowerCase().includes(keyword)
      );
      const successMessage = submit
        ? `Your nomination review for ${details.employee.employeeName || 'the employee'} has been submitted successfully.`
        : isDraftSaveMessage
          ? responseMessage
          : 'Draft saved successfully! Your progress has been saved.';

      setDialog({
        isOpen: true,
        type: 'success',
        title: submit ? 'Submission Successful' : 'Draft Saved',
        message: successMessage,
      });
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string'
          ? error.message
          : 'Unable to save feedback. Please try again.';

      setDialog({
        isOpen: true,
        type: 'error',
        title: submit ? 'Submission Failed' : 'Save Failed',
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeDialog = () => {
    const submittedSuccessfully = dialog.type === 'success' && dialog.title === 'Submission Successful';
    setDialog((current) => ({ ...current, isOpen: false }));
    if (submittedSuccessfully) {
      navigate(`/external-360-feedback/${encodeURIComponent(relationshipId)}/view?uuid=${encodeURIComponent(uuid)}`);
    }
  };

  return (
    <div className="h-full min-h-0 space-y-6 overflow-y-auto pr-1">
      <button type="button" onClick={() => navigate(`/external-360-feedback?uuid=${encodeURIComponent(uuid)}`)} className="flex items-center text-base text-primary-800 transition-colors">
        <img src={arrowBackIcon} alt="Back" className="mr-2 h-4 w-4" />
        Back to Feedback List
      </button>

      {tab === 'view' && (
        <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-4" role="status">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">
                Your nomination review for {details.employee.employeeName || 'the employee'} has been submitted successfully.
              </p>
            </div>
          </div>
        </div>
      )}

      <Card>
        <div className="text-primary-800 mb-4 bg-custom-light-purple p-1 pl-2 text-base font-regular font-verdana">Employee Information</div>
        <div className="grid grid-cols-1 gap-2 px-2 lg:grid-cols-2 lg:gap-8">
          <div className="space-y-3">
            <div className="flex flex-col gap-1 py-2 sm:flex-row sm:py-3">
              <span className="w-40 shrink-0 text-sm text-gray-500">Employee ID</span>
              <span className="text-sm text-gray-900">{details.employee.employeeId}</span>
            </div>
            <div className="flex flex-col gap-1 py-2 sm:flex-row sm:py-3">
              <span className="w-40 shrink-0 text-sm text-gray-500">Designation</span>
              <span className="text-sm text-gray-900">{details.employee.designation}</span>
            </div>
            <div className="flex flex-col gap-1 py-2 sm:flex-row sm:py-3">
              <span className="w-40 shrink-0 text-sm text-gray-500">Department</span>
              <span className="text-sm text-gray-900">{details.employee.department}</span>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex flex-col gap-1 py-2 sm:flex-row sm:py-3">
              <span className="w-40 shrink-0 text-sm text-gray-500">Nomination as</span>
              <span className="text-sm text-gray-900">{details.employee.nominationAs}</span>
            </div>
            <div className="flex flex-col gap-1 py-2 sm:flex-row sm:py-3">
              <span className="w-40 shrink-0 text-sm text-gray-500">Name of Employee</span>
              <NameWithEmailTooltip
                as="span"
                className="text-sm text-gray-900"
                name={details.employee.employeeName}
                email={details.employee.employeeEmail}
              />
            </div>
            <div className="flex flex-col gap-1 py-2 sm:flex-row sm:py-3">
              <span className="w-40 shrink-0 text-sm text-gray-500">Reporting Manager</span>
              <NameWithEmailTooltip
                as="span"
                className="text-sm text-gray-900"
                name={details.employee.reportingManager}
                email={details.employee.reportingManagerEmail}
              />
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] overflow-hidden rounded-lg border-separate border-spacing-0">
              <thead className="bg-purple-800">
                <tr className="text-white">
                  <th className="w-16 px-6 py-3 text-left text-base font-normal">SNO.</th>
                  <th className="px-6 py-3 text-left text-base font-normal">Description/Rating</th>
                  <th className="px-6 py-3 text-center text-base font-normal">1-Excel</th>
                  <th className="px-6 py-3 text-center text-base font-normal">2-Fully Met</th>
                  <th className="px-6 py-3 text-center text-base font-normal">3-Mostly Met</th>
                  <th className="px-6 py-3 text-center text-base font-normal">4-Requires Action</th>
                </tr>
              </thead>
          <tbody>
            {criteria.map((criterion, index) => (
              <tr key={criterion.id} className="bg-white hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{index + 1}.</td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  <div className="flex items-center space-x-2">
                    <span>{criterion.description}</span>
                    {criterion.tooltip && (
                      <Tooltip content={criterion.tooltip}>
                        <svg className="h-4 w-4 cursor-help text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-label={`More information about ${criterion.description}`}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </Tooltip>
                    )}
                  </div>
                </td>
                {[1, 2, 3, 4].map((rating) => (
                  <td key={rating} className="px-6 py-4 text-center">
                    <input
                      type="radio"
                      name={`criterion-${criterion.id}`}
                      value={rating}
                      checked={ratings[criterion.id] === rating}
                      disabled={isReadOnly}
                      onChange={() => setRatings((current) => ({ ...current, [criterion.id]: rating }))}
                      aria-label={`${criterion.description}: ${rating}`}
                      className="h-4 w-4 border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
            </table>
          </div>
        </div>
      </Card>

      <div className="space-y-6">
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Add your Appreciative Feedback for the Employee <span className="text-gray-400">(10-10000 characters)</span>
            <span className={`ml-2 text-xs ${appreciative.trim().length >= 10 ? 'text-green-600' : 'text-red-600'}`}>
              {appreciative.length} characters
            </span>
          </label>
          <textarea
            value={appreciative}
            onChange={(event) => setAppreciative(event.target.value)}
            placeholder="Add your Input"
            readOnly={isReadOnly}
            rows={4}
            className="w-full rounded-md border border-gray-300 p-3 text-sm placeholder-gray-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Add your <strong>Constructive Feedback</strong> for the Employee <span className="text-gray-400">(10-10000 characters)</span>
            <span className={`ml-2 text-xs ${constructive.trim().length >= 10 ? 'text-green-600' : 'text-red-600'}`}>
              {constructive.length} characters
            </span>
          </label>
          <textarea
            value={constructive}
            onChange={(event) => setConstructive(event.target.value)}
            placeholder="Add your Input"
            readOnly={isReadOnly}
            rows={4}
            className="w-full rounded-md border border-gray-300 p-3 text-sm placeholder-gray-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
          />
        </div>
      </div>

      {!isReadOnly && (
        <div className="flex flex-col justify-end gap-3 sm:flex-row">
          <Button
            variant="outline"
            onClick={() => void save(false)}
            disabled={!canSaveDraft || isSubmitting}
            className="border-purple-600 px-6 py-2 text-purple-600 hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : 'Save as Draft'}
          </Button>
          <Button onClick={() => void save(true)} disabled={!isComplete || isSubmitting} className="bg-purple-800 px-6 py-2 text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50">
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
      )}

      <Dialog isOpen={dialog.isOpen} onClose={closeDialog} type={dialog.type} title={dialog.title} message={dialog.message} />
    </div>
  );
};

export default External360FeedbackDetailPage;